/**
 * AI Observability — Calls: every AI call, where it went and where it stopped.
 *
 * Every prompt run and RAG query, with where it went and where it stopped.
 *
 * The page leads with WHERE THINGS BREAK, not with the list: failures counted by
 * the stage they stopped in. A bar that is mostly "Embed the question" says the
 * embedding provider is the problem before anyone opens a row, and clicking it
 * narrows the list to exactly those calls.
 *
 * Warnings sit beside failures on purpose. A RAG answer built on zero retrieved
 * passages, or a prompt that reached the model with a literal {{question}} in
 * it, raises no error and returns 200 — and is usually the real answer to "why
 * was this wrong".
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { aiObservabilityService } from '../../services/ai-observability.service';
import {
  KIND_COLOR, KIND_LABEL, fmtMs, kindLabel, stepLabel, type ObsTraceRow,
} from '../../types/ai-observability.types';
import { CHIP_FAIL, ICONS, fmtWhen, useObsQuery } from './obs-kit';
import { CardHead, ErrorCard, ObsHeader, ObsState } from './obs-shell';
import { MiniWaterfall } from './obs-waterfall';

import '../error-analytics/error-analytics.css';
import './ai-observability.css';

const WARN_CHIP = { background: '#FEF3C7', borderColor: '#FDE68A', color: '#92400E' } as const;

/**
 * The second line under a call's name — whatever identifies THIS call best:
 * the question for a RAG query, the tools an agent reached for.
 */
function subtitle(row: ObsTraceRow): string | null {
  if (row.question) return row.question;
  if (row.kind === 'AGENT') {
    return row.tools?.length
      ? `called ${row.tools.map((t) => (t.calls > 1 ? `${t.name} ×${t.calls}` : t.name)).join(', ')}`
      : 'called no tool';
  }
  return null;
}

/** Failures by stage — the chart that decides which filter to apply. */
function FailureChart({ buckets, active, onPick }: {
  buckets: { step: string; count: number }[];
  active: string | null;
  onPick: (step: string | null) => void;
}) {
  const max = Math.max(1, ...buckets.map((b) => b.count));
  return (
    <section className="ea-card pad" style={{ minWidth: 0, marginBottom: 16 }}>
      <CardHead icon={ICONS.layers} title="Where calls fail"
        sub="Failures grouped by the stage they stopped in. Click a stage to see only those calls."
        right={active ? (
          <button className="obs-btn" onClick={() => onPick(null)} style={{ padding: '4px 10px' }}>
            Clear filter
          </button>
        ) : undefined} />
      {!buckets.length ? (
        <div className="obs-empty">No failures in this window.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
          {buckets.map((b) => {
            const on = active === b.step;
            return (
              <button key={b.step} onClick={() => onPick(on ? null : b.step)} style={{
                all: 'unset', cursor: 'pointer', display: 'grid',
                gridTemplateColumns: 'minmax(150px, 220px) 1fr 40px', gap: 12, alignItems: 'center',
                opacity: active && !on ? 0.45 : 1,
              }}>
                <span style={{ fontSize: 12.5, fontWeight: on ? 600 : 500,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {stepLabel(b.step)}
                </span>
                <span style={{ height: 8, background: 'var(--obs-sunken)', borderRadius: 3 }}>
                  <span style={{ display: 'block', height: '100%', borderRadius: 3,
                    width: `${(b.count / max) * 100}%`,
                    background: b.step === 'unrecorded' ? '#CBD5E1' : '#EF4444' }} />
                </span>
                <span className="num" style={{ fontSize: 12.5, textAlign: 'right',
                  fontVariantNumeric: 'tabular-nums' }}>{b.count}</span>
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
}

/** What happened, in one cell: failed-at, else the first warning, else fine. */
function Outcome({ row }: { row: ObsTraceRow }) {
  if (row.status === 'FAILED') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 300 }}>
        <span className="obs-chip" style={{ ...CHIP_FAIL, alignSelf: 'flex-start' }}>
          failed at {row.failedStep ? stepLabel(row.failedStep) : 'an unrecorded step'}
        </span>
        {row.errorMessage && (
          <span className="ea-sub" style={{ fontSize: 11.5, overflow: 'hidden',
            textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.errorMessage}>
            {row.errorMessage}
          </span>
        )}
      </div>
    );
  }
  if (row.warning) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 300 }}>
        <span className="obs-chip" style={{ ...WARN_CHIP, alignSelf: 'flex-start' }}>
          {row.warningCount} warning{row.warningCount === 1 ? '' : 's'}
        </span>
        <span className="ea-sub" style={{ fontSize: 11.5, overflow: 'hidden',
          textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.warning}>
          {row.warning}
        </span>
      </div>
    );
  }
  return <span className="obs-chip">ok</span>;
}

const ALL = '';

export default function ObsTracing() {
  // Scoped to one prompt or RAG when linked from its view (?promptId=… / ?ragCode=…).
  // The backend narrows to that entity's traces; the page passes it through and says so.
  const [searchParams] = useSearchParams();
  const promptId = searchParams.get('promptId') || undefined;
  const ragCode = searchParams.get('ragCode') || undefined;
  const agentCode = searchParams.get('agentCode') || undefined;
  const [range, setRange] = useState('7d');
  const [kind, setKind] = useState(ALL);
  const [failedOnly, setFailedOnly] = useState(false);
  const [failedStep, setFailedStep] = useState<string | null>(null);
  // Free-text filter. `searchInput` is what the box holds; `search` is the
  // debounced value actually sent, so typing does not fire a request a keystroke.
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const fetchTraces = useCallback(
    () => aiObservabilityService.getTracing({
      timeRange: range, limit: 100,
      ...(promptId ? { promptId } : {}),
      ...(ragCode ? { ragCode } : {}),
      ...(agentCode ? { agentCode } : {}),
      ...(kind ? { kind } : {}),
      ...(failedOnly || failedStep ? { status: 'FAILED' } : {}),
      // "unrecorded" is a bucket label, not a stored step name.
      ...(failedStep && failedStep !== 'unrecorded' ? { failedStep } : {}),
      ...(search ? { search } : {}),
    }),
    [range, promptId, ragCode, agentCode, kind, failedOnly, failedStep, search],
  );
  const { data, loading, error, reload } = useObsQuery(fetchTraces, 'Could not load traces.');

  const s = data?.summary;
  const rows = data?.traces ?? [];

  const kpis = [
    { key: 'c', label: 'Calls', val: s ? s.calls.toLocaleString() : '—' },
    { key: 'f', label: 'Failed', val: s ? String(s.failed) : '—', tone: s?.failed ? '#B91C1C' : undefined },
    { key: 'w', label: 'With warnings', val: s ? String(s.withWarnings) : '—',
      tone: s?.withWarnings ? '#B45309' : undefined },
    // Rows from before step capture. They are listed but cannot say where they
    // went; the number says how much of the window is dark.
    { key: 'u', label: 'Not traced', val: s ? String(s.untraced) : '—' },
  ];

  return (
    <div className="ea-root obs" style={{ padding: 20, minHeight: '100%' }}>
      <ObsHeader
        subtitle="Where every AI call went, and where it stopped — prompts, RAG, agents, evals, judges, ingestion."
        range={range} onRange={setRange} onRefresh={reload} loading={loading}
        meta={s ? `${s.calls.toLocaleString()} calls` : undefined}
      />

      <ErrorCard error={error} gap />

      {(promptId || ragCode || agentCode) && (
        <section className="ea-card pad" style={{ minWidth: 0, marginBottom: 16,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <span className="ea-sub" style={{ fontSize: 12.5 }}>
            {ragCode
              ? `Showing only the traces of RAG ${ragCode}'s queries.`
              : agentCode
                ? `Showing only the traces of agent ${agentCode}.`
                : "Showing only the traces of one prompt's runs."}
          </span>
          <Link to="/ai-observability/tracing" className="obs-btn"
            style={{ padding: '4px 10px', textDecoration: 'none' }}>
            Show all calls
          </Link>
        </section>
      )}

      <div className="ea-kpis" style={{ marginBottom: 16 }}>
        {kpis.map((k) => (
          <div key={k.key} className="ea-card ea-kpi">
            <div className="lab">{k.label}</div>
            <div className="val num" style={k.tone ? { color: k.tone } : undefined}>
              {loading ? '—' : k.val}
            </div>
          </div>
        ))}
      </div>

      {!!data && (
        <FailureChart buckets={data.failuresByStep} active={failedStep}
          onPick={(step) => setFailedStep(step)} />
      )}

      <section className="ea-card" style={{ minWidth: 0, overflow: 'hidden' }}>
        <div style={{ padding: '16px 18px 0' }}>
          <CardHead icon={ICONS.table} title="Calls"
            sub="Newest first. Open one for its full waterfall, the knowledge base behind it, and what the model saw."
            right={
              <div style={{ display: 'flex', gap: 8, flex: 'none', alignItems: 'center' }}>
                <input className="obs-btn" type="search" value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search model, name, provider…" aria-label="Search calls"
                  style={{ padding: '5px 8px', minWidth: 200 }} />
                <select className="obs-btn" value={kind} onChange={(e) => setKind(e.target.value)}
                  style={{ padding: '5px 8px' }} aria-label="Filter by kind">
                  <option value={ALL}>Every AI call</option>
                  {Object.entries(KIND_LABEL).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5,
                  color: 'var(--obs-ink-2)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={failedOnly}
                    onChange={(e) => setFailedOnly(e.target.checked)} />
                  Failed only
                </label>
              </div>
            } />
        </div>

        <ObsState loading={loading} error={error} rows={rows.length}
          empty={search
            ? `No calls match "${search}" in this window.`
            : failedStep
              ? `No calls failed at "${stepLabel(failedStep)}" in this window.`
              : 'No AI calls in this window.'} />

        {!loading && !!rows.length && (
          <div style={{ overflowX: 'auto' }}>
            <table className="obs-tbl">
              <thead>
                <tr>
                  <th style={{ width: 150 }}>When</th>
                  <th>Kind</th>
                  <th>What ran</th>
                  <th>Stages</th>
                  <th>Outcome</th>
                  <th>Model</th>
                  <th className="num">Latency</th>
                  <th style={{ textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.observabilityId}>
                    <td className="obs-mono" style={{ whiteSpace: 'nowrap' }}>
                      <Link to={`/ai-observability/tracing/${row.observabilityId}`}
                        style={{ color: 'var(--obs-ink)', textDecoration: 'none' }}>
                        {fmtWhen(row.startedAt)}
                      </Link>
                    </td>
                    <td>
                      <span className="obs-chip" style={{ color: KIND_COLOR[row.kind],
                        borderColor: 'var(--obs-line)', fontWeight: 600, whiteSpace: 'nowrap' }}>
                        {kindLabel(row.kind)}
                      </span>
                    </td>
                    <td>
                      <div style={{ maxWidth: 260, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Link to={`/ai-observability/tracing/${row.observabilityId}`} style={{
                          fontWeight: 500, color: 'var(--obs-ink)', textDecoration: 'none',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }} title={row.name ?? undefined}>{row.name ?? '—'}</Link>
                        {subtitle(row) && (
                          <span className="ea-sub" style={{ fontSize: 11.5, overflow: 'hidden',
                            textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={subtitle(row) ?? undefined}>
                            {subtitle(row)}
                          </span>
                        )}
                        {row.parentObservabilityId && (
                          <Link to={`/ai-observability/tracing/${row.parentObservabilityId}`}
                            className="ea-sub" style={{ fontSize: 11, textDecoration: 'none' }}>
                            ↳ part of a larger run
                          </Link>
                        )}
                      </div>
                    </td>
                    <td>
                      {row.traced
                        ? <MiniWaterfall steps={row.steps} />
                        : <span className="ea-sub" title="Recorded before step capture existed."
                          style={{ fontSize: 11.5, fontStyle: 'italic' }}>not traced</span>}
                    </td>
                    <td><Outcome row={row} /></td>
                    <td style={{ whiteSpace: 'nowrap', fontSize: 12 }}>{row.modelId ?? '—'}</td>
                    <td className="num">{fmtMs(row.latencyMs)}</td>
                    <td style={{ textAlign: 'center' }}>
                                    <Link to={`/ai-observability/tracing/${row.observabilityId}`} aria-label="View"
                                      title="View" data-testid="ObsTracing.Action.View" style={{ display: 'inline-flex', padding: 6 }}>
                                      <i className="pi pi-eye text-blue-600" style={{ fontSize: '1.05rem' }} />
                                    </Link>
                                  </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
